import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckCheck, MessageSquare } from 'lucide-react'
import { CountBadge } from '../kit'
import { useUnread, snippet, chatWhen } from '../lib/chat.js'
import { openChatDock } from '../lib/chatDock.js'
import { nameInitial } from '../lib/text.js'

/**
 * ข้อความ in the main sidebar — the standing list of patient threads with
 * something the caller has not read.
 *
 * Deliberately a POPOVER and not a route. There is nothing to do on an inbox
 * page that you do not do better on the patient itself, and a route would have
 * to be gated, navigated to, and then navigated away from. Each row opens the
 * ข้อความ dock over whatever you are already looking at — same as the toast,
 * never a new tab.
 *
 * The count comes from the shared store that `ChatToaster` loads and the
 * realtime inbox refreshes, so this never fetches on its own.
 */

const PANEL_W = 340

// Where the panel goes. Desktop: a flyout to the RIGHT of the sidebar, its top
// level with the trigger. It used to drop straight down, which covered the menu
// it belongs to and left half of it on the page, underneath the page's own
// controls (a search box cut the first row's time off). Portalled to <body> so
// nothing on the page can paint over it. No room to the right — the phone
// off-canvas menu — and it drops below the trigger instead.
function usePanelPosition(open, triggerRef) {
  const [pos, setPos] = useState(null)
  const place = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const edge = el.closest('aside')?.getBoundingClientRect().right ?? r.right
    const vw = window.innerWidth
    const vh = window.innerHeight
    if (edge + 8 + PANEL_W <= vw - 8) {
      const top = Math.max(8, r.top - 4)
      setPos({ left: edge + 8, top, width: PANEL_W, maxH: vh - top - 16 })
    } else {
      const left = Math.max(8, r.left)
      const top = r.bottom + 6
      setPos({ left, top, width: Math.min(PANEL_W, vw - left - 8), maxH: vh - top - 16 })
    }
  }, [triggerRef])
  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place) }
  }, [open, place])
  return pos
}

export default function ChatInboxTrigger({ collapsed = false, tone = 'light' }) {
  const unread = useUnread()
  const dark = tone === 'dark'
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const pos = usePanelPosition(open, triggerRef)

  useEffect(() => {
    if (!open) return
    // The panel is portalled, so "outside" means outside BOTH it and the trigger.
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const n = unread.total_unread
  const threads = [...unread.threads].sort((a, b) => (Number(b.last_at) || 0) - (Number(a.last_at) || 0))
  // Same rule as the toast: open the dock here, never a new page.
  const openDock = (t) => {
    setOpen(false)
    openChatDock({ patientId: t.patient_id, patientName: t.fullname || '' })
  }

  return (
    <>
      {/* Same size, weight and states as the nav rows below it, so it reads as
          one of the menu — and the count sits on the right like theirs. Only the
          icon-only rail puts it on the icon, where there is nowhere else. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={n ? `ข้อความใหม่ ${n} ข้อความ` : 'ข้อความ'}
        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[14.5px] font-medium transition-colors ${
          dark
            ? (open ? 'bg-white/15 text-white' : n > 0 ? 'text-white hover:bg-white/10' : 'text-brand-100/85 hover:bg-white/10 hover:text-white')
            : (open ? 'bg-slate-100 text-slate-900' : n > 0 ? 'text-slate-900 hover:bg-slate-50' : 'text-slate-600 hover:bg-slate-50')
        } ${collapsed ? 'justify-center' : ''}`}
      >
        <span className="relative flex-none">
          <MessageSquare size={17} strokeWidth={1.75} />
          {collapsed && n > 0 && (
            <span className={`absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10.5px] font-bold leading-4 text-center tabular-nums ring-2 ${dark ? 'ring-brand-900' : 'ring-white'}`}>
              {n > 99 ? '99+' : n}
            </span>
          )}
        </span>
        {!collapsed && <span className="flex-1 truncate text-left">ข้อความ</span>}
        {!collapsed && n > 0 && (
          <span className="flex-none inline-flex items-center justify-center h-[18px] min-w-[18px] px-1.5 rounded-full text-[11px] font-semibold leading-none tabular-nums bg-rose-500 text-white">
            {n > 99 ? '99+' : n}
          </span>
        )}
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="ข้อความที่ยังไม่ได้อ่าน"
          style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxH }}
          className="z-[60] flex flex-col rounded-xl bg-white ring-1 ring-slate-200 shadow-xl shadow-slate-900/10 overflow-hidden animate-ss-pop-in"
        >
          <div className="flex-none flex items-baseline gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100">
            <span className="flex-1 text-[14px] font-semibold text-slate-900">ข้อความที่ยังไม่ได้อ่าน</span>
            {n > 0 && <span className="flex-none text-[12px] text-slate-400 tabular-nums">{n} ข้อความ</span>}
          </div>

          {threads.length === 0 ? (
            <div className="px-4 py-8 flex flex-col items-center gap-2 text-slate-400">
              <CheckCheck size={20} strokeWidth={1.75} className="text-emerald-500" />
              <span className="text-[13px]">อ่านครบแล้ว</span>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto p-1.5">
              {threads.map((t) => {
                const name = t.fullname || `ผู้ป่วย #${t.patient_id}`
                return (
                  <button
                    key={t.patient_id}
                    type="button"
                    onClick={() => openDock(t)}
                    className="w-full flex items-start gap-3 px-2.5 py-2.5 rounded-lg text-left hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none transition-colors"
                  >
                    <span className="flex-none mt-0.5 w-9 h-9 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center text-[14px] font-semibold">
                      {nameInitial(t.fullname)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-baseline gap-2">
                        <span className="flex-1 truncate text-[13.5px] font-semibold text-slate-900">{name}</span>
                        <span className="flex-none text-[11.5px] text-slate-400 tabular-nums">{chatWhen(t.last_at)}</span>
                      </span>
                      <span className="mt-0.5 flex items-start gap-2">
                        <span className="flex-1 min-w-0 text-[12.5px] text-slate-500 leading-snug line-clamp-2 break-words">
                          <span className="font-medium text-slate-600">{t.last_author || '—'}:</span> {snippet(t.last_body, 80)}
                        </span>
                        <span className="flex-none mt-px"><CountBadge n={Number(t.unread) || 0} tone="alert" /></span>
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Never let a cap read as completeness. */}
          {unread.truncated && (
            <p className="flex-none px-4 py-2 border-t border-amber-100 bg-amber-50 text-[12px] text-amber-800 leading-snug">
              แสดงได้ไม่ครบ — มีข้อความมากกว่าที่นับไว้ในช่วง 7 วันล่าสุด
            </p>
          )}
        </div>,
        document.body,
      )}
    </>
  )
}
